import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import styles from './components.module.css';

export function MarqueeText({
  children,
  className,
  active = false,
}: {
  children: ReactNode;
  className?: string;
  /** When true, marquee while overflowing (in addition to hover). */
  active?: boolean;
}) {
  const outerRef = useRef<HTMLSpanElement>(null);
  const innerRef = useRef<HTMLSpanElement>(null);
  const [distance, setDistance] = useState(0);

  useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;

    const measure = () => {
      // Ellipsis + max-width can make scrollWidth === clientWidth; unwrap to measure.
      const previousMaxWidth = inner.style.maxWidth;
      const previousAnimation = inner.style.animation;
      inner.style.maxWidth = 'none';
      inner.style.animation = 'none';
      const next = Math.max(0, Math.ceil(inner.scrollWidth - outer.clientWidth));
      inner.style.maxWidth = previousMaxWidth;
      inner.style.animation = previousAnimation;
      setDistance((current) => (current === next ? current : next));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(outer);
    observer.observe(inner);
    return () => observer.disconnect();
  }, [children]);

  const overflowing = distance > 0;
  const duration = Math.max(3, distance / 28);

  return (
    <span
      ref={outerRef}
      className={[styles.marquee, className].filter(Boolean).join(' ')}
      data-overflow={overflowing ? 'true' : 'false'}
      data-active={active && overflowing ? 'true' : 'false'}
      style={
        {
          '--marquee-distance': `${distance}px`,
          '--marquee-duration': `${duration}s`,
        } as CSSProperties
      }
    >
      <span ref={innerRef} className={styles.marqueeInner}>
        {children}
      </span>
    </span>
  );
}
